<template>
  <div ref="field" class="signature-pad-field">
    <canvas ref="canvas" class="signature-pad-canvas" tabindex="-1"></canvas>
    <div class="signature-pad-baseline" aria-hidden="true"></div>
    <button type="button" class="signature-pad-clear" @click.stop="clear">Clear</button>
  </div>
</template>

<script>
var CANVAS_HEIGHT = 180

export default {
  name: 'SignaturePad',
  data: function () {
    return {
      drawing: false,
      hasInk: false,
      lastX: 0,
      lastY: 0,
      sized: false,
      inputMode: null,
      suppressMouseUntil: 0
    }
  },
  mounted: function () {
    this.inputMode = this.detectInputMode()
    this.bindListeners()
    this.prime()
  },
  beforeUnmount: function () {
    this.unbindListeners()
  },
  methods: {
    detectInputMode: function () {
      var ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
      var isIOS = /iPad|iPhone|iPod/.test(ua) ||
        (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      var isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|Chromium/i.test(ua)
      if (isIOS) return 'touch'
      if (isSafari) return 'mouse'
      if (typeof window !== 'undefined' && 'ontouchstart' in window && navigator.maxTouchPoints > 0) return 'touch'
      return 'mouse'
    },
    bindListeners: function () {
      var canvas = this.$refs.canvas
      if (!canvas) return

      this._onMouseDown = this.handleMouseDown.bind(this)
      this._onMouseMove = this.handleMouseMove.bind(this)
      this._onMouseUp = this.handleMouseUp.bind(this)
      this._onTouchStart = this.handleTouchStart.bind(this)
      this._onTouchMove = this.handleTouchMove.bind(this)
      this._onTouchEnd = this.handleTouchEnd.bind(this)

      if (this.inputMode === 'touch') {
        canvas.addEventListener('touchstart', this._onTouchStart, { passive: false, capture: true })
        canvas.addEventListener('touchmove', this._onTouchMove, { passive: false })
        canvas.addEventListener('touchend', this._onTouchEnd, { passive: false })
        canvas.addEventListener('touchcancel', this._onTouchEnd, { passive: false })
      } else {
        canvas.addEventListener('mousedown', this._onMouseDown, true)
        window.addEventListener('mousemove', this._onMouseMove)
        window.addEventListener('mouseup', this._onMouseUp)
      }
    },
    unbindListeners: function () {
      var canvas = this.$refs.canvas
      if (!canvas) return
      if (this._onTouchStart) {
        canvas.removeEventListener('touchstart', this._onTouchStart)
        canvas.removeEventListener('touchmove', this._onTouchMove)
        canvas.removeEventListener('touchend', this._onTouchEnd)
        canvas.removeEventListener('touchcancel', this._onTouchEnd)
      }
      if (this._onMouseDown) {
        canvas.removeEventListener('mousedown', this._onMouseDown)
        window.removeEventListener('mousemove', this._onMouseMove)
        window.removeEventListener('mouseup', this._onMouseUp)
      }
    },
    prime: function () {
      var self = this
      var attempt = 0
      var trySize = function () {
        attempt++
        self.sizeCanvas()
        if (!self.sized && attempt < 40) {
          window.requestAnimationFrame(trySize)
        }
      }
      this.$nextTick(function () {
        trySize()
        window.setTimeout(trySize, 0)
        window.setTimeout(trySize, 50)
        window.setTimeout(trySize, 150)
        window.setTimeout(trySize, 300)
      })
    },
    layoutWidth: function () {
      var field = this.$refs.field
      if (!field) return 300
      var w = field.clientWidth || field.offsetWidth || 0
      if (w < 1 && field.parentElement) {
        w = field.parentElement.clientWidth || 0
      }
      return Math.max(w, 300)
    },
    sizeCanvas: function () {
      var canvas = this.$refs.canvas
      if (!canvas) return false

      var layoutWidth = this.layoutWidth()
      var ratio = window.devicePixelRatio || 1
      var backingWidth = Math.max(1, Math.floor(layoutWidth * ratio))
      var backingHeight = Math.max(1, Math.floor(CANVAS_HEIGHT * ratio))

      if (this.sized && canvas.width === backingWidth && canvas.height === backingHeight) {
        return true
      }

      var snapshot = this.hasInk ? canvas.toDataURL('image/png') : null
      canvas.width = backingWidth
      canvas.height = backingHeight
      canvas.style.width = layoutWidth + 'px'
      canvas.style.height = CANVAS_HEIGHT + 'px'

      var ctx = canvas.getContext('2d')
      if (!ctx) return false
      this.applyContext(ctx)

      if (snapshot) {
        var img = new Image()
        var self = this
        img.onload = function () {
          self.applyContext(ctx)
          ctx.drawImage(img, 0, 0, layoutWidth, CANVAS_HEIGHT)
        }
        img.src = snapshot
      }

      this.sized = true
      return true
    },
    applyContext: function (ctx) {
      var ratio = window.devicePixelRatio || 1
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.lineWidth = 2
      ctx.strokeStyle = '#000000'
      ctx.fillStyle = '#000000'
    },
    pointFromClient: function (clientX, clientY) {
      var canvas = this.$refs.canvas
      var rect = canvas.getBoundingClientRect()
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      }
    },
    markInk: function () {
      if (!this.hasInk) {
        this.hasInk = true
        this.$emit('ink-change', true)
      }
    },
    drawDot: function (ctx, x, y) {
      ctx.beginPath()
      ctx.arc(x, y, 1.25, 0, Math.PI * 2)
      ctx.fill()
    },
    emitSignature: function () {
      if (!this.hasInk || !this.$refs.canvas) return
      this.$emit('update:modelValue', this.$refs.canvas.toDataURL('image/png'))
    },
    beginStroke: function (x, y) {
      this.sizeCanvas()
      this.drawing = true
      this.lastX = x
      this.lastY = y
      var ctx = this.$refs.canvas.getContext('2d')
      this.applyContext(ctx)
      this.drawDot(ctx, x, y)
      this.markInk()
    },
    continueStroke: function (x, y) {
      if (!this.drawing || !this.$refs.canvas) return
      var ctx = this.$refs.canvas.getContext('2d')
      this.applyContext(ctx)
      ctx.beginPath()
      ctx.moveTo(this.lastX, this.lastY)
      ctx.lineTo(x, y)
      ctx.stroke()
      this.lastX = x
      this.lastY = y
      this.markInk()
    },
    endStroke: function () {
      if (!this.drawing) return
      this.drawing = false
      this.emitSignature()
    },
    handleMouseDown: function (event) {
      if (event.button !== 0) return
      if (Date.now() < this.suppressMouseUntil) return
      event.preventDefault()
      this.sizeCanvas()
      var point = this.pointFromClient(event.clientX, event.clientY)
      this.beginStroke(point.x, point.y)
    },
    handleMouseMove: function (event) {
      if (!this.drawing) return
      event.preventDefault()
      var point = this.pointFromClient(event.clientX, event.clientY)
      this.continueStroke(point.x, point.y)
    },
    handleMouseUp: function () {
      this.endStroke()
    },
    handleTouchStart: function (event) {
      event.preventDefault()
      this.suppressMouseUntil = Date.now() + 800
      this.sizeCanvas()
      var touch = event.changedTouches[0] || event.touches[0]
      if (!touch) return
      var point = this.pointFromClient(touch.clientX, touch.clientY)
      this.beginStroke(point.x, point.y)
    },
    handleTouchMove: function (event) {
      if (!this.drawing) return
      event.preventDefault()
      var touch = event.changedTouches[0] || event.touches[0]
      if (!touch) return
      var point = this.pointFromClient(touch.clientX, touch.clientY)
      this.continueStroke(point.x, point.y)
    },
    handleTouchEnd: function () {
      this.endStroke()
    },
    clear: function () {
      var canvas = this.$refs.canvas
      if (!canvas) return
      var ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      this.applyContext(ctx)
      this.hasInk = false
      this.drawing = false
      this.$emit('ink-change', false)
      this.$emit('update:modelValue', '')
    }
  }
}
</script>
